import _ from 'underscore';
import Backbone from 'backbone';
import Api from '@/lib/api/api';
import v5_Model from '@/models/v5/model';
import Grant from '@/models/v5/grant';
import Product from '@/models/v5/product';
import v5_AccountMetrics from '@/models/v5/account_metrics';
import v5_Collection from '@/collections/v5/collection';
import AccountRoles from '@/collections/v5/account_roles';
import GrantsCollection from '@/collections/v5/grants';
import Devices from '@/collections/v5/devices';
import Invitations from '@/collections/v5/invitations';
import Invites from '@/collections/v5/invites';
import counts_collection from '@/collections/v5/counts';
import LoginKeys from '@/collections/v5/login_keys';
import moment from 'moment-timezone';
import ide_settings from '@/models/ide_settings';

function account_target_match(account, grant){
    return grant.rel.get('target').id === account.id;
}
function account_grantee_match(account, grant){
    return grant.rel.get('grantee').id === account.id;
}

function create_devices_collection(account) {
    var c = new v5_Collection();
    c.fetch = function fetch(options) {
        options = _.extend({
            url: Api.urls.devices_for_account({
                account_id: account.id
            })
        }, options);
        return Devices.fetch(options);
    };
    // Listen for additions or omissions from the global Devices
    // collection and determine if the device needs to be a part
    // of this collection
    function on_add_to_global_devices(device) {
        if (device.rel.get('owner') === account) {
            c.add(device, { merge: true });
        }
    }
    function on_remove_from_global_devices(device) {
        c.remove(device);
    }
    function on_change_device(device) {
        var owner = device.rel.get('owner');
        if (owner && owner.id === account.id) {
            c.add(device, { merge: true });
        } else {
            c.remove(device);
        }
    }
    c.listenTo(Devices, 'add', on_add_to_global_devices);
    c.listenTo(Devices, 'remove', on_remove_from_global_devices);
    c.listenTo(Devices, 'change', on_change_device);
    // Keep devices in this list sorted by name
    c.on('change:name', function() { this.comparator && this.sort(); });
    return c;
}
function create_account_metrics(account) {
    var model = new v5_AccountMetrics();
    model.url = function() {
        return Api.urls.account_metrics({ id: account.id });
    };
    model.sync = function(method, model, options) {
        return Api.sync(method, model, options);
    };
    return model;
}
function create_cell_count() {
    var model = new Backbone.Model();
    model.url = function() {
        return Api.urls.cellcount();
    };
    model.sync = function(method, model, options) {
        return Api.sync(method, model, options);
    };
    return model;
}

function create_capabilities(account) {
    var collection = new v5_Collection();
    collection.model = Grant;
    collection.url = function() {
        return Api.urls.account_capabilities({ id: account.id });
    };
    /**
     * To make magic like fetching and destroying work, we
     * need to set each grant fetched from this collection
     * to be part of the global GrantsCollection.
     */
    function add_to_grants_collection(grant) {
        GrantsCollection.add(grant);
        grant.collection = GrantsCollection;
    }
    collection.on('add', add_to_grants_collection);

    collection.sync = function(method, model, options) {
        return Api.sync(method, model, options);
    };

    return collection;
}

/**
 * Create a collection of proxies. A proxy is a rollup
 * of grants associated with a single target account.
 * Proxy: {
 *     id: target_account.id
 *     target: common_target_account
 *     grants: [ grant1, grant2, etc. ]
 * }
 *
 * While this could be represented as a rollup of roles, 
 * for internal management and for operations like 'delete',
 * it's easier to have a handle on the grant directly.
 *
 * @param account which is the grantee for all the grants
 *
 */
function create_proxy_collection(account) {
    var coll = new Backbone.Collection();
    /**
     * When a grant is added to the GrantsCollection, find the target
     * among our proxies and add the grant.
     */
    function on_add_grant(grant) {
        const target = grant.rel.get('target');
        const grantee = grant.rel.get('grantee');
        // Only add grants to this collection if they have been
        // granted to this account, and if we have valid target
        // and grantee.
        if ( !target || !grantee || grantee.id !== account.id ) return;
        var proxy = coll.find(target.id);
        if (!proxy) {
            proxy = coll.add({
                id: target.id,
                target: target,
                grants: []
            });
            proxy.destroy = function destroy() {
                let all = [];
                proxy.get('grants').forEach(function(g) {
                    all.push(
                        g.destroy({
                            wait: true,
                        })
                    );
                });
                return Promise.all(all);
            };
        }
        var grants = proxy.get('grants');
        if (!grants.includes(grant)) {
            proxy.set('grants', grants.concat([grant]));
        }
    }
    /**
     * When a grant is removed from the GrantsCollection, find the
     * target among our proxies and remove the grant.
     */
    function on_remove_grant(grant) {
        var target = grant.rel.get('target');
        if (!target) return;
        var proxy = coll.get(target.id);
        if (!proxy) return;
        var grants = proxy.get('grants');
        if (grants.includes(grant)) {
            grants = grants.filter(function(g) { return g !== grant; });
            proxy.set('grants', grants);
        }
        if (proxy.get('grants').length === 0) coll.remove(proxy);
    }
    account.listenTo(GrantsCollection, 'add', on_add_grant);
    // TODO: figure out why this event isn't being listened to
    account.listenTo(GrantsCollection, 'remove destroy', on_remove_grant);

    GrantsCollection.each(on_add_grant);
    return coll;
}
var Account = v5_Model.extend({
    initialize: function() {
        // supplementary collections and info
        this.devices = create_devices_collection(this);
        this.metrics = create_account_metrics(this);
        this.cellcount = create_cell_count();
        this.counts = counts_collection.create_counts_model_for(
            this, Api.urls.account_counts, Product.type
        );

        this.capabilities = create_capabilities(this);

        // A collection of accounts we can proxy as
        this.new_proxies = create_proxy_collection(this);
        this.proxies = new Backbone.Collection();
        this.capabilities.each(_.bind(this.on_add_grant, this));
        this.listenTo(this.capabilities, 'add', this.on_add_grant);
        this.listenTo(this.capabilities, 'remove', this.on_remove_grant);
        GrantsCollection.each(_.bind(this.on_add_grant, this));
        this.listenTo(GrantsCollection, 'add', this.on_add_grant);
        this.listenTo(GrantsCollection, 'remove', this.on_remove_grant);

        // Update proxy property on newly added devices
        // (as they might have come from other accounts)
        this.listenTo(this.devices, 'add', function(device) {
            device.proxy = this.id;
        });
        this.permissions_by_account = {};

        this.type = Account.type;
        // set up devicegroup profiles
        this.listenTo(this.proxies, 'add', function(proxie){ proxie.init_devicegroup_profiles(); });
        // after the account has sync'd, then we need to initialize the device group profiles
        this.listenToOnce(this, 'sync', function(){ this.init_devicegroup_profiles(); }.bind(this));
    },
    sync: function (method, model, options) {
        options = (options ? options : {});
        options.headers = _.extend({
            'Content-type': 'application/vnd.api+json'
        }, options.headers);

        if (!options.attrs) {
            var jsonapi = model.toJsonApi && model.toJsonApi();
            if (jsonapi) options.attrs = { data: jsonapi };
        }
        if (options.patch) {
            let attrs = {
                data: {
                    id: model.id,
                    type: model.type,
                    attributes: options.attrs
                }
            };
            options.attrs = attrs;
        }
        return Api.sync(method, model, options);
    },
    grant: function(grantee, role, options) {
        return GrantsCollection.grant(this, grantee, role, options);
    },

    // GET /v5/accounts/me/permissions should give me all my permissions (tied to user's tier)
    // GET /v5/accounts/:id/permissions should give me all my permissions on another user's 
    // account (collaboration permissions, tied to user's role and target's tier)
    // Can't assume, as we did in the past, that we have all "permissions" on our own account -
    // permits is the new way to check both whether we can do an action on another account,
    // as well as whether we are high-tier enough to do a given action.
    // For example, tier 1 accounts can't make production devicegroups, because they don't pay us.
    permits: function(target_account, permission){
        if (permission) {
            // Permissions on API don't follow devicegroup type labelling, so we have to handle that.
            let modified_permission = permission.replace('fixture', '');
            return this.permissions_by_account[target_account.id].includes(modified_permission);
        } else {
            if (!this.permissions_by_account[target_account.id]) {
                this.set_permissions_for_account(target_account, []);
            }
            return this.permissions_by_account[target_account.id];
        }
    },
    set_permissions_for_account(account, permissions_array) {
        this.permissions_by_account[account.id] = permissions_array;
    },
    fetch_permissions_for_account(account) {
        return Api.ajax_v5({
            url: Api.urls.permissions({id: account.id}),
            type: 'get'
        }).done((data)=>{
            if (!data.meta) {
                document.location.reload();
            }
            account.set_permissions_for_account(this, data.meta.permissions);
        });
    },
    // Returns true if any permissions in the array are granted
    permits_any: function(account, permissions) {
        return _.some(permissions, function(permission) {
            return this.permits(account, permission);
        }, this);
    },
    // Return the roles this account has granted to another account
    roles_for: function(that_account) {
        var this_account = this;
        return _.compact(GrantsCollection.map(function(g){
            var match = g.rel.get('target').id == this_account.id
                        && g.rel.get('grantee').id == that_account.id;
            return match && g.rel.get('role');
        }));
    },
    flags: function(flag) {
        if (flag) {
            return _.contains(this.get('flags'), flag);
        } else {
            return this.get('flags');
        }
    },
    grants: function() {
        return GrantsCollection.filter(
            _.partial(account_target_match, this)
        );
    },
    capabilities: function() {
        return GrantsCollection.filter(
            _.partial(account_grantee_match, this)
        );
    },
    invitations: function() {
        return Invitations.filter(
            _.partial(account_target_match, this)
        );
    },
    invites: function() {
        return Invites.filter(
            _.partial(account_target_match, this)
        );
    },
    get_usage: function(date_gt, date_lt) {
        let d1 = new Date(date_gt);
        d1.setTime( d1.getTime() - 1000000 );
        const d2 = new Date(date_lt);

        const url = Api.urls.account_usage({id: this.id}) + '?page[number]=1&filter[timestamp]=gt:'+d2.toISOString()+'&filter[timestamp]=lt:'+d1.toISOString()+'&page[size]=100';
        let usage = new v5_Collection();

        return usage.fetch_all({ url: url });
    },
    get_usage_by_device: function(device_id) {
        return Api.ajax_v5({
            url: Api.urls.account_usage({id: this.id}),
            type: 'get',
            data: {
                device_id
            }
        }).done((resp)=>{
            return resp;
        });
    },
    change_password: function(new_password, current_password) {
        var headers = Api.getHeaderDefaults();
        const id = this.id;
        const type = this.type;
        headers['X-Electricimp-Password'] = current_password;
        return Api.ajax_v5({
            data: JSON.stringify({
                data: {
                    attributes: {
                        password: new_password
                    },
                    id: id,
                    type: type,
                }
            }),
            headers: headers,
            url: Api.urls.account_password_change({ id }),
            type: 'patch'
        });
    },
    change_account_details: function(options){
        var headers = Api.getHeaderDefaults();
        return this.save(
            options,
            {
                patch: true,
                headers: headers,
                url: Api.urls.account_details_change({id: this.id}),
        });
    },
    // Request to return totp_uri and verification_token. This is the first step to enabling MFA.
    request_mfa_verification: function(options) {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';
        headers['X-Electricimp-Password'] = (options && options.current_password);

        return Api.ajax({
            headers: headers,
            url: Api.urls.auth_totp(),
            type: 'POST'
        });
    },
    // Removes mfa access from the account`
    remove_mfa: function(options) {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';
        headers['X-Electricimp-Password'] = options.current_password;

        return Api.ajax({
            headers: headers,
            url: Api.urls.auth_totp(),
            type: 'DELETE'
        });
    },
    // Get recovery keys for account
    recovery_keys: function () {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';

        return Api.ajax_v5({
            headers: headers,
            url: Api.urls.auth_recovery_keys(),
            type: 'GET',
            wait: true
        });
    },
    regenerate_recovery_keys: function (options) {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';
        headers['X-Electricimp-Password'] = options.current_password;

        return Api.ajax_v5({
            headers: headers,
            url: Api.urls.auth_recovery_keys(),
            type: 'POST',
        });
    },
    /**
     * Second part of MFA activation scheme
     * @param options Object - include totp code from an authenticator app for the header
     * and the verification_token string from request_mfa_verification method.
     */
    confirm_totp_code: function(options) {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';
        headers['x-electricimp-otp-token'] = options.totp_code;
        var data = {
            verification_token: options.verification_token,
        };
        
        return Api.ajax_v5({
            headers: headers,
            url: Api.urls.auth_verify_totp(),
            type: 'POST',
            data: JSON.stringify(data),
        });
    },
    /**
     * Create a new login key for me account
     */
    create_login_key: function(options) {
        return LoginKeys.create_login_key(options);
    },
    // Whenever a grant is added to one of the collections of grants
    // we listen to, add it to our internal collection.
    on_add_grant: function(grant) {
        var is_proxy_grant = grant.rel.get('grantee')
                            && grant.rel.get('grantee').type === Account.type
                            && grant.rel.get('grantee').id === this.id
                            ;
        if (!is_proxy_grant) return;
        if (!grant.rel.get('target')) return console.warn("Expected grant %s to have a target account", grant.id);
        this.proxies.add(grant.rel.get('target'), { merge: true });
    },
    // Whenever a grant is removed from one of the collections of grants
    // we listen to, remove it from our internal collection.
    on_remove_grant: function(grant) {
        this.proxies.remove(grant);
    },
    /**
     * Create an invitation to collaborate on this account, given an
     * email address and a set of roles.
     * Performs an XHR call.
     * @param String an email address
     * @param roles an array of Roles
     * @param options like Backbone.Model.fetch options
     * @return Invitation
     */
    create_invitation: function(identifier, roles, options) {
        return Invites.create_invitation(identifier, this, roles, options);
    },
    get_oldest_grant_date: function(grantor) {
        var grant_dates = [];
        grantor.grants().forEach(function(grant) {
            if (grant.rel.get('grantee').id === this.id) {
                grant_dates.push(grant.get('created_at'));
            }
        }, this);
        grant_dates.sort();
        return grant_dates[0];
    },
    change_email: function(new_email, password){
        return this.save(
            { email: new_email, }, 
            {
                patch: true,
                headers: { 'X-Electricimp-Password': password, },
                url: Api.urls.account_email_change({id: this.id}),
        });
    },
    toggle_require_mfa_for_collaborators(required){
        return this.save({ collaborator_mfa_required: required}, {patch: true, wait: true});
    },
    toggle_require_mfa_for_production(required){
        return this.save({ deployment_mfa_required: required}, {patch: true, wait: true});
    },
    /**
     * Send API a stripe token from a valid credit card response 
     */
    send_stripe_token: function(token) {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';
        return Api.ajax_v5({
            data: JSON.stringify(token),
            headers: headers,
            url: Api.urls.billing_source(),
            type: 'POST',
        });
    },
    /**
     * Get billing data about this account
     */
    get_payments_data: function() {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';
        return Api.ajax_v5({
            url: Api.urls.billing(),
            type: 'GET',
            wait: true,
        });
    },
    /**
     * Get Account Invoices
     */
    get_invoices: function() {
        return Api.ajax_v5({
            url: Api.urls.invoices(),
            type: 'GET',
            wait: true,
        });
    },
    /**
     * Get a Stripe Payment Intent from EI API
     */
    get_stripe_payment_intent: function() {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';
        return Api.ajax_v5({
            headers: headers,
            url: Api.urls.billing() + '/method',
            type: 'GET',
            wait: true,
        });
    },
    /**
     * Send a Stripe payment method to EI API for this
     * account that reflects an udpated payment method
     * from stripe.handleCardSetup     
     */
    post_stripe_payment_method: function(result) {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';
        let request_body = {
            payment_method: result.setupIntent.payment_method
        };

        return Api.ajax_v5({
            headers: headers,
            data: JSON.stringify(request_body),
            url: Api.urls.billing() + '/method',
            type: 'POST',
            wait: true,
        });
    },
    /**
     * Ask EI API to delete all Stripe payment method(s)
     * and Stripe customer for this account
     */
    delete_stripe_payment_method: function(result) {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';

        return Api.ajax_v5({
            headers: headers,
            url: Api.urls.billing() + '/method',
            type: 'DELETE',
            wait: true,
        });
    },
    // returns string of timezone from ui/settings
    get_timezone: function() {
        return ide_settings.get('timezone');
    },
    set_timezone: function(timezone) {  // timezone = String from user selection
        var moment_tzarray = moment.tz.names();
        var selected_timezone = timezone;
        // verify if user selected tz is valid moment timezone the ES6 way :)
        if (moment_tzarray.includes(selected_timezone)) {
            // whatever the user selected set that as timezone and save
            ide_settings.set('timezone', selected_timezone);
            ide_settings.save(); 
        }
    },
    devicegroup_profiles: undefined,
    get_devicegroup_profiles: function() {
        // let's memoize! devicegroup profiles change *super* rarely.. only look
        // them up once for each individual, and don't allow multiple in-flight
        // promises for a given ID
        let result = Account.cached_profiles[this.id];
        if (result == null) {
            const that = this;
            result = Api.ajax_v5({
                headers: Api.getHeaderDefaults(),
                url: Api.urls.devicegroup_profiles({id: this.id}),
                type: 'GET',
                wait: true
            }).catch(function(err) {
                // if it's an error, forget our cached promise
                delete Account.cached_profiles[that.id];
                return Promise.reject(err);
            });
            // track this promise; existence means we're in-flight or completed successfully
            Account.cached_profiles[this.id] = result;
        }

        return result;
    },
    init_devicegroup_profiles: function() {
        this.get_devicegroup_profiles().then(
            function(resp){
                this.devicegroup_profiles = resp.data;
                if ( resp.data && resp.data.length == 0 ){
                    this.devicegroup_profiles = undefined;
                } else {
                    this.devicegroup_profiles.unshift({
                        id: undefined,
                        attributes: {
                            name: 'Default'
                        }
                    });
                }
            }.bind(this),
            function(err){
                this.devicegroup_profiles = undefined;
            }.bind(this)
        );
    }

});
// The v5 type
Account.type = 'account';
// The roles available for any account
Account.roles = AccountRoles;
// Enumerated flags
Account.flags = {
    
};
// The default account state (right now, the only account state)
Account.defaultState = 'active';
Account.cached_profiles = {};

export default Account;
